/**
 * useFixtureContext — React glue for the data service layer.
 *
 * Owns the lifecycle of the IDataService adapter so consumers
 * (SceneControls' MaestroProvider mount) get a stable reference
 * that they can hand to the form library without triggering Provider
 * teardown on every fixture switch.
 *
 * Identity stability is the load-bearing detail here:
 *   - `dataService` is a wrapper object whose reference is the SAME
 *     for the entire hook lifetime within a given source. Its
 *     `getContext()` delegates to the latest underlying adapter,
 *     which IS rebuilt on fixtureId change.
 *   - `catalogue` is rebuilt only when the source changes; stable
 *     across fixtureId changes.
 *
 * Why a wrapper instead of just returning the underlying adapter?
 * If we returned the underlying adapter directly, every fixtureId
 * change would hand <MaestroProvider> a new dataService object,
 * which the form library treats as a meaningful change (memo deps)
 * and would re-fire its orchestrator effects unnecessarily.
 *
 * subscribe() is owned at the WRAPPER level, not delegated to the
 * underlying adapter. That's deliberate: when the operator picks a
 * new fixture the underlying adapter is swapped, but the form
 * library's listener was registered once and must survive the swap.
 * On every fixture/source change the wrapper fires its subscribers
 * so the form re-resolves its bindings against the new fixture
 * IN PLACE — without the caption being remounted.
 */
import { useEffect, useMemo, useRef } from "react";
import { createDataServices } from "../services/data/dataServiceFactory";

export function useFixtureContext({
  source,
  fixtureId,
  apolloClient,
  ddsClient,
  fifaConfig,
  plsConfig,
}) {
  // Catalogue + the createContext factory rebuild when source (or its
  // transport deps) change. OPUS-1045: plsConfig is ALSO a dep — the PLS bind
  // context is competition+season-scoped and SceneControls now pins a
  // per-fixture competition+season on Connect. Without this, services.create-
  // Context would close over a STALE plsConfig and a PLS→PLS competition switch
  // would bind the new fixture against the previous competition/season.
  // Identity stability is unaffected: the value handed to consumers is the
  // stable wrapper (dataServiceRef) below, NOT `services`; rebuilding services
  // only swaps the underlying adapter + notifies subscribers (the intended
  // re-resolve). fifaConfig is intentionally NOT a dep — browsing FIFA
  // competitions must not rebuild a live FIFA bind (pre-existing, out of scope).
  const services = useMemo(
    () => createDataServices({
      source,
      apolloClient,
      ddsClient,
      fifaConfig,
      plsConfig,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, apolloClient, ddsClient, plsConfig],
  );

  // Mutable cell holding the *current* underlying IDataService.
  // Rebuilt on every fixtureId change; the stable wrapper below
  // reads through it so callers never see the reference change.
  const underlyingRef = useRef(null);
  // Initialise synchronously so the very first getContext() call
  // (which can race the effect) has a real adapter to delegate to.
  if (underlyingRef.current === null) {
    underlyingRef.current = services.createContext(fixtureId);
  }

  // Wrapper-level subscriber set — the source of truth for who wants
  // to be told the context changed. Listeners register HERE, not on
  // the underlying adapter, so they survive the underlying being
  // swapped on a fixture change.
  const listenersRef = useRef(new Set());
  const notify = () => {
    listenersRef.current.forEach((l) => {
      try { l(); } catch { /* a bad listener must not break the rest */ }
    });
  };

  // Swap the underlying adapter when source/fixture changes, then tell
  // every subscriber so the form library re-resolves against the new
  // fixture — without the caption being remounted. The first run
  // (mount) only wires the transport bridge; there's nothing to
  // re-resolve and the form resolves on mount anyway.
  const firstRunRef = useRef(true);
  useEffect(() => {
    underlyingRef.current = services.createContext(fixtureId);
    // Bridge any transport-level pushes (e.g. DDS WS) from the new
    // adapter through to the wrapper subscribers.
    const unsub = underlyingRef.current.subscribe(notify);
    if (firstRunRef.current) {
      firstRunRef.current = false;
    } else {
      notify();
    }
    return typeof unsub === "function" ? unsub : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, fixtureId]);

  // Stable wrapper. Same object reference for the hook's lifetime.
  // The ref pattern guarantees fresh delegation on every call.
  const dataServiceRef = useRef(null);
  if (dataServiceRef.current === null) {
    dataServiceRef.current = {
      getContext: () => underlyingRef.current.getContext(),
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
      getState: () => underlyingRef.current.getState
        ? underlyingRef.current.getState()
        : undefined,
    };
  }

  return {
    dataService: dataServiceRef.current,
    catalogue: services.catalogue,
  };
}
